import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fullErrorText } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlideOver, SlideOverContent } from "@/components/ui/slide-over";
import { Skill, SkillStatus, skillApi } from "./skill.api";
import { descriptionLengthError } from "./skillMarkdown.utils";

export function SkillRepositoryEditor({
  skill,
  onClose,
  onSaved,
}: {
  skill: Skill;
  onClose: () => void;
  onSaved?: (updated: Skill) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(skill.name);
  const [description, setDescription] = useState(skill.description);
  const [status, setStatus] = useState(skill.status);
  const trimmedName = name.trim();
  const nameError = !trimmedName
    ? "请输入 Skill 名称"
    : trimmedName !== skill.name && !/^[A-Za-z0-9_-]{3,64}$/.test(trimmedName)
      ? "Skill 名称仅支持 3-64 位英文、数字、连字符(-) 和下划线(_)"
      : "";
  const descriptionError = descriptionLengthError(description, 1000);
  const mutation = useMutation({
    mutationFn: () =>
      skillApi.updateSkill(skill.id, {
        request: { name: trimmedName, description: description.trim(), status: Number(status) },
      }),
    onSuccess: updated => {
      onSaved?.(updated);
      void queryClient.invalidateQueries({ queryKey: ["skills-management"] });
      void queryClient.invalidateQueries({ queryKey: ["skills-plaza-infinite"] });
      toast.success("仓库信息已保存");
      onClose();
    },
    onError: error => toast.error(fullErrorText(error)),
  });
  return (
    <SlideOver
      open
      onOpenChange={open => {
        if (!open && !mutation.isPending) onClose();
      }}
      title="编辑 Skill"
      description="更新 Skill 基础信息。"
      actions={
        <Button
          disabled={mutation.isPending || Boolean(nameError || descriptionError)}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "保存中" : "保存"}
        </Button>
      }
    >
      <SlideOverContent>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">名称 *</p>
          <Input
            aria-label="Skill 名称"
            placeholder="3-64 位英文/数字/-/_"
            value={name}
            disabled={mutation.isPending}
            aria-invalid={Boolean(nameError)}
            onChange={e => setName(e.target.value)}
          />
          {nameError && (
            <p role="alert" className="text-sm text-destructive">
              {nameError}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">仓库描述</p>
          <textarea
            aria-label="仓库描述"
            placeholder="介绍整个 Skill 的用途与范围"
            value={description}
            disabled={mutation.isPending}
            onChange={e => setDescription(e.target.value)}
            className="w-full min-h-20 rounded-md border border-input px-3 py-2 text-sm bg-background"
          />
          {descriptionError && (
            <p role="alert" className="text-sm text-destructive">
              {descriptionError}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">状态</p>
          <select
            aria-label="状态"
            value={status}
            disabled={mutation.isPending}
            onChange={e => setStatus(Number(e.target.value) as SkillStatus)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value={SkillStatus.AVAILABLE}>可用</option>
            <option value={SkillStatus.UNAVAILABLE}>不可用</option>
            <option value={SkillStatus.UPLOADING}>上传中</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">标签</p>
          <Input aria-label="标签" value={(skill.tags || []).join(", ")} readOnly />
        </div>
      </SlideOverContent>
    </SlideOver>
  );
}
